import { z } from "zod";
import { zAnyNonNullish } from "../zod/utils.js";

import { DisplayTableColumnId } from "../idTypeBrands.js";
import { getNormalEnum } from "../pseudoEnums.js";

import {
  BinaryColumnPredicate,
  BinaryColumnPredicateOpLiteral,
  ListBinaryColumnPredicate,
  ListBinaryColumnPredicateOpLiteral,
  UnaryColumnPredicate,
  UnaryColumnPredicateOpLiteral,
} from "./columnPredicateTypes.js";

export const ConditionalFormattingRulesetTypeLiteral = z.enum([
  "GRADIENT",
  "RULES",
]);
export type ConditionalFormattingRulesetType = z.infer<
  typeof ConditionalFormattingRulesetTypeLiteral
>;
export const ConditionalFormattingRulesetType = getNormalEnum(
  ConditionalFormattingRulesetTypeLiteral,
);

const ConditionalFormattingColumnFilterTypeLiteral = z.enum([
  "EXCLUDE",
  "INCLUDE",
]);
export type ConditionalFormattingColumnFilterType = z.infer<
  typeof ConditionalFormattingColumnFilterTypeLiteral
>;
export const ConditionalFormattingColumnFilterType = getNormalEnum(
  ConditionalFormattingColumnFilterTypeLiteral,
);

export const ExcludeColumnFilter = z.object({
  type: z.literal(ConditionalFormattingColumnFilterType.EXCLUDE),
  excludedColumns: z.array(DisplayTableColumnId),
});

export const IncludeColumnFilter = z.object({
  type: z.literal(ConditionalFormattingColumnFilterType.INCLUDE),
  includedColumns: z.array(DisplayTableColumnId),
});

export const ConditionalFormattingColumnFilter = z.union([
  /**
   * Note: Elements of excludedColumns/includedColumns refer to DataFrame
   * column names _before_ renames are applied. In some parts of the code
   * (like the UI) these values are referred to as columnIds.
   */
  ExcludeColumnFilter,
  IncludeColumnFilter,
]);
export type ConditionalFormattingColumnFilter = z.infer<
  typeof ConditionalFormattingColumnFilter
>;

// TODO(EXP-1346): Consider adding the workspace palette colors as
// an option to the conditional formatting styles.
export const ConditionalFormattingCellStyleLiteral = z.enum([
  "RED",
  "ORANGE",
  "YELLOW",
  "GREEN",
  "BLUE",
  "PURPLE",
  "PINK",
  "LIME",
  "GRAY",
  "TURQUOISE",
]);
export type ConditionalFormattingCellStyle = z.infer<
  typeof ConditionalFormattingCellStyleLiteral
>;
export const ConditionalFormattingCellStyle = getNormalEnum(
  ConditionalFormattingCellStyleLiteral,
);

export type ConditionalFormattingCellColors = {
  [key in ConditionalFormattingCellStyle]: {
    backgroundColor: string;
    color: string;
  };
};

// TODO: define more gradient types
const ConditionalFormattingGradientStyleLiteral = z.enum([
  "GREEN_WHITE",
  "WHITE_GREEN",
  "RED_WHITE",
  "WHITE_RED",
  "GREEN_WHITE_RED",
  "RED_WHITE_GREEN",
  "GREEN_YELLOW_RED",
  "RED_YELLOW_GREEN",
  "BLUE_WHITE",
  "WHITE_BLUE",
  "RED_WHITE_BLUE",
  "BLUE_WHITE_RED",
]);
export type ConditionalFormattingGradientStyle = z.infer<
  typeof ConditionalFormattingGradientStyleLiteral
>;
export const ConditionalFormattingGradientStyle = getNormalEnum(
  ConditionalFormattingGradientStyleLiteral,
);

export type ConditionalFormattingGradientColors = {
  [key in ConditionalFormattingGradientStyle]: {
    colors: string[];
  };
};

const BaseConditionalFormattingRuleset = z.object({
  type: ConditionalFormattingRulesetTypeLiteral,
  columns: ConditionalFormattingColumnFilter,
});

/* eslint-disable tree-shaking/no-side-effects-in-initialization */
export const GradientConditionalFormattingRuleset =
  BaseConditionalFormattingRuleset.extend({
    type: z.literal(ConditionalFormattingRulesetType.GRADIENT),
    style: ConditionalFormattingGradientStyleLiteral,
  });
export type GradientConditionalFormattingRuleset = z.infer<
  typeof GradientConditionalFormattingRuleset
>;

export const ConditionalFormattingColumnPredicateOpLiteral = z.union([
  BinaryColumnPredicateOpLiteral,
  UnaryColumnPredicateOpLiteral,
  ListBinaryColumnPredicateOpLiteral,
]);
export type ConditionalFormattingColumnPredicateOp = z.infer<
  typeof ConditionalFormattingColumnPredicateOpLiteral
>;

const ConditionalFormattingColumnPredicate = z.union([
  UnaryColumnPredicate,
  BinaryColumnPredicate,
  ListBinaryColumnPredicate,
]);

export const RulesConditionalFormattingRulesetRule = z.object({
  expression: ConditionalFormattingColumnPredicate,
  style: ConditionalFormattingCellStyleLiteral,
});
export type RulesConditionalFormattingRulesetRule = z.infer<
  typeof RulesConditionalFormattingRulesetRule
>;

export const RulesConditionalFormattingRuleset =
  BaseConditionalFormattingRuleset.extend({
    type: z.literal(ConditionalFormattingRulesetType.RULES),
    rules: z.array(RulesConditionalFormattingRulesetRule),
  });
export type RulesConditionalFormattingRuleset = z.infer<
  typeof RulesConditionalFormattingRuleset
>;

export const ConditionalFormattingRuleset = z.union([
  GradientConditionalFormattingRuleset,
  RulesConditionalFormattingRuleset,
]);
export type ConditionalFormattingRuleset = z.infer<
  typeof ConditionalFormattingRuleset
>;

export const ConditionalFormattingDefinition = z.object({
  rulesets: z.array(ConditionalFormattingRuleset),
});
export type ConditionalFormattingDefinition = z.infer<
  typeof ConditionalFormattingDefinition
>;

// #region Types for conditional formatting rules after the server has computed metadata
export const GradientConditionalFormattingRulesetWithMetadata =
  GradientConditionalFormattingRuleset.extend({
    metadata: z.object({
      // The smallest value in all the gradient data
      min: z.number(),
      // The largest value in all the gradient data
      max: z.number(),
    }),
  });
export type GradientConditionalFormattingRulesetWithMetadata = z.infer<
  typeof GradientConditionalFormattingRulesetWithMetadata
>;

export const RulesConditionalFormattingRulesetWithMetadata =
  RulesConditionalFormattingRuleset.extend({
    // nothing meaningful the server needs to calculate for these as of now
    metadata: zAnyNonNullish(),
  });
export type RulesConditionalFormattingRulesetWithMetadata = z.infer<
  typeof RulesConditionalFormattingRulesetWithMetadata
>;

export const ConditionalFormattingRulesetWithMetadata = z.union([
  GradientConditionalFormattingRulesetWithMetadata,
  RulesConditionalFormattingRulesetWithMetadata,
]);
export type ConditionalFormattingRulesetWithMetadata = z.infer<
  typeof ConditionalFormattingRulesetWithMetadata
>;

export const ConditionalFormattingDefinitionWithMetadata = z.object({
  rulesets: z.array(ConditionalFormattingRulesetWithMetadata),
});
export type ConditionalFormattingDefinitionWithMetadata = z.infer<
  typeof ConditionalFormattingDefinitionWithMetadata
>;
// #endregion
